(() => {
  const FALLBACK_COLORS = [
    { name: "Lime Green", sku: "SV570610", screenSampleHex: "#B1C74E" },
    { name: "Navy Blue", sku: "SV570608", screenSampleHex: "#303C4E" },
    { name: "Royal Blue", sku: "SV570605", screenSampleHex: "#2E419B" },
    { name: "Kelly Green", sku: "SV573835", screenSampleHex: "#188B54" },
    { name: "Yellow", sku: "SV570599", screenSampleHex: "#EACC40" },
    { name: "Red", sku: "SV570603", screenSampleHex: "#D1353A" },
    { name: "Black", sku: "SV570593", screenSampleHex: "#272526" },
  ];

  const money = (cents, currency = "USD") =>
    new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);

  const parseHex = (value = "#777777") => {
    const normalized = String(value).trim().replace(/^#/, "");
    const expanded = normalized.length === 3
      ? normalized.split("").map((character) => character + character).join("")
      : normalized.padEnd(6, "7").slice(0, 6);
    return [0, 2, 4].map((offset) => Number.parseInt(expanded.slice(offset, offset + 2), 16) || 0);
  };

  const finishKey = (value = "") => {
    const normalized = String(value).toLowerCase();
    if (normalized.includes("custom")) return "custom";
    if (normalized.includes("black")) return "black";
    if (normalized.includes("raw") || normalized.includes("aluminum") || normalized.includes("diy")) return "raw";
    return normalized;
  };

  class GamedayCornholeSingle extends HTMLElement {
    connectedCallback() {
      if (this.dataset.cornholeProductReady === "true") return;
      this.dataset.cornholeProductReady = "true";

      this.form = this.querySelector("form");
      this.variantInput = this.querySelector("[data-cornhole-variant-id]");
      this.variantData = this.readJson("[data-cornhole-variants]");
      this.finishInputs = Array.from(this.querySelectorAll("[data-cornhole-finish-option]"));
      this.boardColorInput = this.querySelector("[data-cornhole-board-color]");
      this.boardHexOutput = this.querySelector("[data-cornhole-board-hex]");
      this.customColorPanel = this.querySelector("[data-cornhole-custom-color]");
      this.boardRender = this.querySelector("[data-cornhole-board-render]");
      this.boardImages = Array.from(this.querySelectorAll("[data-cornhole-board-image]"));
      this.priceOutput = this.querySelector("[data-cornhole-price]");
      this.submitButton = this.querySelector("[data-cornhole-submit]");
      this.submitLabel = this.querySelector("[data-cornhole-submit-label]");
      this.proofFileInput = this.querySelector("[data-cornhole-proof-file]");
      this.proofStatusInput = this.querySelector("[data-cornhole-proof-status]");
      this.bagSourceImage = this.querySelector("[data-cornhole-bag-source]");
      this.bagPreviewCanvas = this.querySelector("[data-cornhole-bag-preview]");
      this.bagPreviewToken = 0;
      this.swatchGrid = this.querySelector("[data-cornhole-bag-swatches]");
      this.searchInput = this.querySelector("[data-cornhole-bag-search]");
      this.checkoutEnabled = this.dataset.checkoutEnabled === "true";
      this.productName = this.dataset.productName || "Gameday Cornhole Board";
      this.productRoute = this.dataset.productRoute || this.productName;
      this.assetId = this.dataset.assetId || "GFCB";
      this.boardSize = this.dataset.boardSize || "24 × 48 IN";
      this.bagSize = this.dataset.bagSize || "6 × 6 IN";
      this.boardQuantity = Number(this.dataset.boardQuantity || 1);
      this.bagQuantity = Number(this.dataset.bagQuantity || 4);
      this.propertyInputs = Object.fromEntries(
        Array.from(this.querySelectorAll("[data-cornhole-property]")).map((input) => [input.dataset.cornholeProperty, input]),
      );
      this.colors = window.EXOGamedayBagColorMatchData?.colors?.length
        ? window.EXOGamedayBagColorMatchData.colors
        : FALLBACK_COLORS;
      this.selectedBag = this.colors.find((color) => color.name === "Lime Green") || this.colors[0];

      if (!new URLSearchParams(window.location.search).has("variant")) {
        const chartreuseFinish = this.finishInputs.find((input) => finishKey(input.value) === "custom");
        if (chartreuseFinish) chartreuseFinish.checked = true;
      }

      this.finishInputs.forEach((input) => input.addEventListener("change", () => this.update()));
      this.boardColorInput?.addEventListener("input", () => {
        if (this.boardRender) this.boardRender.dataset.customEdited = "true";
        this.update();
      });
      this.searchInput?.addEventListener("input", () => this.renderSwatches(this.searchInput.value));
      this.form?.addEventListener("submit", (event) => this.captureProofOnSubmit(event));

      this.renderSwatches();
      this.update();
    }

    readJson(selector) {
      const node = this.querySelector(selector);
      if (!node?.textContent) return [];
      try {
        return JSON.parse(node.textContent);
      } catch (_error) {
        return [];
      }
    }

    currentFinish() {
      return this.finishInputs.find((input) => input.checked)?.value || "Raw Aluminum";
    }

    boardColor(finish) {
      const key = finishKey(finish);
      if (key === "black") return "#171817";
      if (key === "custom") return (this.boardColorInput?.value || "#A6D90D").toUpperCase();
      return "#BCC4C5";
    }

    findVariant(finish) {
      const key = finishKey(finish);
      return this.variantData.find((variant) =>
        variant.options?.some((option) => finishKey(option) === key) || finishKey(variant.title) === key,
      );
    }

    renderSwatches(query = "") {
      if (!this.swatchGrid) return;
      const normalized = query.trim().toLowerCase();
      const colors = this.colors.filter((color) =>
        !normalized || `${color.name} ${color.sku}`.toLowerCase().includes(normalized),
      );

      this.swatchGrid.replaceChildren();
      colors.forEach((color) => {
        const button = document.createElement("button");
        const chip = document.createElement("i");
        const label = document.createElement("span");
        button.type = "button";
        button.setAttribute("aria-pressed", String(color.sku === this.selectedBag?.sku));
        button.setAttribute("aria-label", `${color.name}, FWD ${color.sku}`);
        chip.style.setProperty("--swatch", color.screenSampleHex || "#777777");
        label.textContent = color.name;
        button.append(chip, label);
        button.addEventListener("click", () => {
          this.selectedBag = color;
          this.renderSwatches(this.searchInput?.value || "");
          this.update();
        });
        this.swatchGrid.append(button);
      });
    }

    update() {
      const finish = this.currentFinish();
      const key = finishKey(finish);
      const boardColor = this.boardColor(finish);
      const bag = this.selectedBag || FALLBACK_COLORS[0];
      const variant = this.findVariant(finish);
      const configuration = `${finish} / ${bag.name} ${bag.sku} / ${this.bagQuantity} × ${this.bagSize} BAGS`;

      this.style.setProperty("--couch-board-color", boardColor);
      this.style.setProperty("--couch-bag-color", bag.screenSampleHex || "#303C4E");
      if (this.boardRender) this.boardRender.dataset.finish = key;
      this.boardImages.forEach((image) => {
        image.hidden = image.dataset.cornholeBoardImage !== key;
      });
      if (!this.boardImages.some((image) => !image.hidden)) {
        const rawImage = this.boardImages.find((image) => image.dataset.cornholeBoardImage === "raw");
        if (rawImage) rawImage.hidden = false;
      }
      if (this.boardHexOutput) this.boardHexOutput.textContent = boardColor;
      if (this.customColorPanel) this.customColorPanel.dataset.active = String(key === "custom");
      this.querySelectorAll('[data-finish-chip*="custom"]').forEach((chip) => chip.style.setProperty("--swatch", boardColor));

      this.writeText("[data-cornhole-summary-finish]", finish);
      this.writeText("[data-cornhole-summary-bags]", `${bag.name} / ${bag.sku}`);
      this.writeText("[data-cornhole-selected-bag-name]", bag.name);
      this.writeText("[data-cornhole-selected-bag-sku]", `FWD ${bag.sku}`);
      this.writeText("[data-cornhole-bag-preview-name]", bag.name);
      this.writeText("[data-cornhole-configuration]", configuration.toUpperCase());
      this.querySelector("[data-cornhole-selected-bag-chip]")?.style.setProperty("--swatch", bag.screenSampleHex || "#303C4E");
      this.renderBagPreview(bag).catch((error) => {
        console.warn(`${this.productName} bag preview could not be recolored.`, error);
      });

      if (variant && this.variantInput) this.variantInput.value = variant.id;
      if (this.checkoutEnabled && variant) {
        if (this.priceOutput) this.priceOutput.textContent = money(variant.price, "USD");
        if (this.submitButton) this.submitButton.disabled = !variant.available;
        if (this.submitLabel) this.submitLabel.textContent = variant.available ? "ADD THE GAME TO CART" : "SELECTED GAME UNAVAILABLE";
      } else {
        if (this.submitButton) this.submitButton.disabled = true;
        if (this.submitLabel) this.submitLabel.textContent = "RELEASE CONFIGURATION PENDING";
      }

      this.setProperty("configuration", configuration);
      this.setProperty("board-finish", finish);
      this.setProperty("board-color", key === "custom" ? boardColor : finish);
      this.setProperty("bag-color", bag.name);
      this.setProperty("bag-sku", bag.sku);
    }

    async renderBagPreview(bag) {
      const source = this.bagSourceImage;
      const canvas = this.bagPreviewCanvas;
      if (!source || !canvas) return;

      const previewKey = `${bag.sku}:${bag.screenSampleHex}`;
      if (canvas.dataset.previewKey === previewKey) return;
      const token = ++this.bagPreviewToken;

      if (!source.complete || !source.naturalWidth) {
        if (typeof source.decode === "function") {
          await source.decode();
        } else {
          await new Promise((resolve, reject) => {
            source.addEventListener("load", resolve, { once: true });
            source.addEventListener("error", reject, { once: true });
          });
        }
      }
      if (token !== this.bagPreviewToken || !source.naturalWidth) return;

      const width = Math.min(source.naturalWidth, 1254);
      const height = Math.round(width * source.naturalHeight / source.naturalWidth);
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) return;

      context.drawImage(source, 0, 0, width, height);
      const image = context.getImageData(0, 0, width, height);
      const pixels = image.data;
      const target = parseHex(bag.screenSampleHex || "#303C4E");

      for (let offset = 0; offset < pixels.length; offset += 4) {
        const red = pixels[offset];
        const green = pixels[offset + 1];
        const blue = pixels[offset + 2];
        const dominance = green - Math.max(red, blue);
        if (green < 34 || dominance < 9) continue;

        const strength = Math.min(1, Math.max(0, (dominance - 9) / 58));
        const luminance = red * 0.2126 + green * 0.7152 + blue * 0.0722;
        const shade = Math.min(1.38, Math.max(0.24, luminance / 132));
        for (let channel = 0; channel < 3; channel += 1) {
          const recolored = Math.min(255, target[channel] * shade);
          pixels[offset + channel] = Math.round(pixels[offset + channel] * (1 - strength) + recolored * strength);
        }
      }

      context.putImageData(image, 0, 0);
      canvas.dataset.previewKey = previewKey;
      canvas.setAttribute("aria-label", `${this.bagQuantity} ${bag.name} ${this.bagSize} Gameday Flags cornhole bags`);
    }

    async captureProofOnSubmit(event) {
      if (!this.checkoutEnabled) {
        event.preventDefault();
        return;
      }
      if (this.form?.dataset.cornholeProofReady === "true") return;
      if (!this.form || !this.proofFileInput || typeof DataTransfer === "undefined") return;

      event.preventDefault();
      const submitter = event.submitter || this.submitButton;
      const variant = this.findVariant(this.currentFinish());
      if (!variant?.available) return;

      this.submitButton?.setAttribute("aria-busy", "true");
      if (this.submitButton) this.submitButton.disabled = true;

      try {
        const proof = await this.renderCartProof();
        const transfer = new DataTransfer();
        const proofName = `${this.assetId.toLowerCase()}-${Date.now()}-cart-proof.png`;
        transfer.items.add(new File([proof], proofName, { type: "image/png", lastModified: Date.now() }));
        this.proofFileInput.files = transfer.files;
        if (this.proofStatusInput) this.proofStatusInput.value = "Board and coordinated-bag cart proof attached";
      } catch (error) {
        console.error(`${this.productName} cart proof capture failed.`, error);
        if (this.proofStatusInput) this.proofStatusInput.value = "Live selection saved / image capture unavailable";
      }

      this.form.dataset.cornholeProofReady = "true";
      this.submitButton?.removeAttribute("aria-busy");
      if (this.submitButton) this.submitButton.disabled = false;
      this.form.requestSubmit(submitter || undefined);
    }

    async renderCartProof() {
      const finish = this.currentFinish();
      const key = finishKey(finish);
      const boardColor = this.boardColor(finish);
      const bag = this.selectedBag || FALLBACK_COLORS[0];
      const sourceImage = this.boardImages.find((image) => image.dataset.cornholeBoardImage === key)
        || this.boardImages.find((image) => image.dataset.cornholeBoardImage === "raw");
      if (!sourceImage) throw new Error("The authored board render is unavailable.");
      if (!sourceImage.complete || !sourceImage.naturalWidth) await sourceImage.decode();

      const canvas = document.createElement("canvas");
      canvas.width = 1600;
      canvas.height = 1000;
      const context = canvas.getContext("2d");
      if (!context) throw new Error("The cart proof canvas is unavailable.");

      context.fillStyle = "#101411";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.strokeStyle = "rgba(188, 196, 197, 0.08)";
      for (let x = 0; x <= canvas.width; x += 80) {
        context.beginPath(); context.moveTo(x, 0); context.lineTo(x, canvas.height); context.stroke();
      }
      for (let y = 0; y <= canvas.height; y += 80) {
        context.beginPath(); context.moveTo(0, y); context.lineTo(canvas.width, y); context.stroke();
      }

      context.fillStyle = "#A6D90D";
      context.font = "800 22px Arial, sans-serif";
      context.fillText(`${this.assetId} / SAVED CART PROOF`, 76, 72);
      context.fillStyle = "#FFFFFF";
      context.font = "800 48px Arial, sans-serif";
      context.fillText(this.productName, 76, 126, 1420);

      const boardFrame = { x: 64, y: 160, width: 1040, height: 740 };
      context.drawImage(sourceImage, boardFrame.x, boardFrame.y, boardFrame.width, boardFrame.height);
      if (key === "custom") {
        context.save();
        context.globalCompositeOperation = "color";
        context.fillStyle = boardColor;
        context.fillRect(boardFrame.x, boardFrame.y, boardFrame.width, boardFrame.height);
        context.restore();
      }

      context.fillStyle = "#A6D90D";
      context.font = "800 18px Arial, sans-serif";
      context.fillText("BOARD", 1138, 206);
      context.fillStyle = "#FFFFFF";
      context.font = "800 31px Arial, sans-serif";
      context.fillText(finish.toUpperCase(), 1138, 245, 400);
      context.fillStyle = "#BCC4C5";
      context.font = "700 20px Arial, sans-serif";
      context.fillText(key === "custom" ? boardColor : `${this.boardSize} / AUTHORED ${this.assetId}`, 1138, 278, 390);

      context.fillStyle = "#A6D90D";
      context.font = "800 18px Arial, sans-serif";
      context.fillText(`${this.bagQuantity} × ${this.bagSize} BAGS`, 1138, 342);
      context.fillStyle = "#FFFFFF";
      context.font = "800 31px Arial, sans-serif";
      context.fillText(bag.name.toUpperCase(), 1138, 381, 400);
      context.fillStyle = "#BCC4C5";
      context.font = "700 20px Arial, sans-serif";
      context.fillText(`OTTERTEX / ${bag.sku}`, 1138, 414);

      const bagColor = bag.screenSampleHex || "#303C4E";
      const bagPositions = [
        { x: 1170, y: 530, rotation: -0.14 }, { x: 1290, y: 500, rotation: 0.1 },
        { x: 1245, y: 645, rotation: 0.08 }, { x: 1370, y: 625, rotation: -0.1 },
        { x: 1165, y: 755, rotation: 0.11 }, { x: 1290, y: 770, rotation: -0.08 },
        { x: 1380, y: 750, rotation: 0.07 }, { x: 1430, y: 510, rotation: -0.05 },
      ];
      bagPositions.slice(0, this.bagQuantity).forEach(({ x, y, rotation }) => {
        context.save();
        context.translate(x + 56, y + 56);
        context.rotate(rotation);
        this.roundedRect(context, -56, -56, 112, 112, 18);
        context.fillStyle = bagColor;
        context.fill();
        context.strokeStyle = "rgba(255, 255, 255, 0.34)";
        context.lineWidth = 3;
        context.stroke();
        context.restore();
      });

      context.fillStyle = "rgba(16, 20, 17, 0.96)";
      context.fillRect(0, 926, canvas.width, 74);
      context.strokeStyle = "#A6D90D";
      context.lineWidth = 3;
      context.beginPath(); context.moveTo(0, 926); context.lineTo(canvas.width, 926); context.stroke();
      context.fillStyle = "#FFFFFF";
      context.font = "800 20px Arial, sans-serif";
      context.fillText(`${finish.toUpperCase()} / ${bag.name.toUpperCase()} ${bag.sku} / ${this.boardQuantity} BOARD + ${this.bagQuantity} BAGS`, 76, 970, 1450);

      return await new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("The cart proof could not be encoded."))), "image/png", 0.94);
      });
    }

    roundedRect(context, x, y, width, height, radius) {
      const safeRadius = Math.min(radius, width / 2, height / 2);
      context.beginPath();
      context.moveTo(x + safeRadius, y);
      context.lineTo(x + width - safeRadius, y);
      context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
      context.lineTo(x + width, y + height - safeRadius);
      context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
      context.lineTo(x + safeRadius, y + height);
      context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
      context.lineTo(x, y + safeRadius);
      context.quadraticCurveTo(x, y, x + safeRadius, y);
      context.closePath();
    }

    writeText(selector, value) {
      const node = this.querySelector(selector);
      if (node) node.textContent = value;
    }

    setProperty(key, value) {
      if (this.propertyInputs[key]) this.propertyInputs[key].value = value;
    }
  }

  if (!customElements.get("gameday-cornhole-single")) {
    customElements.define("gameday-cornhole-single", GamedayCornholeSingle);
  }
})();
